
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit
}                       from '@angular/core';

import { AuthenticationService } from '@/services/authentication.service';
import { AlertService }          from '@/services/alert.service';
import { User }                  from "@/models/user";
import { Logger                } from '@/util/logger';
import {empty, errorAsText} from '@/util/util';
import { Subscription          } from 'rxjs';
import {
  Router
}                                from '@angular/router';
import {first} from "rxjs/operators";
import {ActionService} from "@/services/action.service";

@Component({
  standalone: false,
  selector:    '#nav-bar',
  templateUrl: 'navigation.bar.component.html'
})

/**
 *
 * @class NavigationBarComponent
 *
 * @brief stamp out the navigation menu at the top of the page
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class NavigationBarComponent implements OnInit, OnDestroy, AfterViewInit {

  /** the current logged-in user */
  public user: User;

  /** the user's profile pic / avatar, if theere is one */
  public profilePic: string = '<img src="assets/images/profile-512.png">';

  /** the observable for the logged-in user */
  private userWatch: Subscription;

  /** the count-down to expiry */
  public expiresLabel: string = '';

  /** id for our count-down timer */
  private countDownTimer: number = 0;

  /**
   * standard constructor
   *
   * @param auth    the user service
   * @param alerts  the alert manager/service
   * @param router  in case we need to redirect the page
   * @param actions the GUI actions we can invoke
   */
  constructor(
    private auth:    AuthenticationService,
    private alerts:  AlertService,
    private router:  Router,
    private actions: ActionService
  ) {

  }

  /* on creation subscribe to the alert service so we get updates when alerts happen from anywhere */
  ngOnInit() {

    /* grab the current user, we need to know if they are an administrator or not */

    this.user = this.auth.userOfSession;

    /*
     * watch for the logged-in user to go null, it means we've been logged out
     * somehow, session timeout, SSO validation fail, or manual logout.
     *
     */

    this.userWatch = this.auth.observableUser
      .subscribe(user => {

        Logger.debug('[NavigationBar] user switched to: ', user);

        this.user = user;

        return;
      });

  }

  ngAfterViewInit() {

    this.setTimer();
  }

  /** test if the current user is an administrator */
  isAdmin(): boolean {

    if (!this.user) {

      return false;
    }

    return this.user.isAdmin;
  }

  /* clean up at the end */
  ngOnDestroy() {

    /* stop watching the logged-in user */

    this.userWatch.unsubscribe();
  }


  /** (re)set the count-down timer for when our session expires */
  setTimer() {

    let nb: NavigationBarComponent = this;

    if (nb.countDownTimer) {

      window.clearInterval(nb.countDownTimer);

    }

    /*
     * every second update the count-down timer for session expiry...if we are within
     * 3 seconds of logout time, auto-logout.
     *
     */

    nb.countDownTimer = window.setInterval(() => {

      if (!nb.user) {

        nb.user = nb.auth.userOfSession;

      }

      if (!nb.user) {

        /* no user yet */

        return;
      }

      /* set the profile pic if we can */

      nb.profilePic = `<img class="userprofileicon" src="/assets/images/profile-512.png">`;

      if ((typeof nb.user.options != 'undefined') && (nb.user.options != null)) {

        if ((typeof nb.user.options.picture != 'undefined') &&
            (nb.user.options.picture != null) &&
            !empty(nb.user.options.picture)) {

          nb.profilePic = `<img class="userprofileicon"  src="${nb.user.options.picture}">`;
        }

      }

      /* check for session expiry */

      const now: number = Math.floor(Date.now() / 1000);

      if (now >= nb.user.expiryAt) {

        /* require login, session expired */

        window.clearInterval(nb.countDownTimer);

        nb.countDownTimer = 0;

        nb.user = null;

        nb.alerts.info(`Session expired, requiring login...`, {autoClose: true});

        nb.router.navigate(['/login'], {queryParams: {returnUrl: '/overwatch'}});

        return;
      }

      const delta = nb.user.expiryAt - now;

      if (delta === 59) {

        /* 1 minute warning */

        nb.alerts.warn(`Session expires in 60 seconds`, {autoClose: true});

      }

      if (delta === 29) {

        /* 30 second warning */

        nb.alerts.error(`Session expires in 30 seconds`, {autoClose: true});

      }
      if (delta === 9) {

        /* 10 second warning */

        nb.alerts.error(`Session expires in 10 seconds`, {autoClose: true});

      }

      const h = Math.floor(delta / 3600);
      const m = Math.floor(delta % 3600 / 60);
      const s = Math.floor(delta % 3600 % 60);

      nb.expiresLabel = `<span class="countdown-time">${h}:${m}:${s}</span>`;

      if (delta <= 60) {

        nb.expiresLabel = `<span class="countdown-time countdown-warning">${h}:${m}:${s}</span>`;
      }

      if (delta <= 30) {

        nb.expiresLabel = `<span class="countdown-time countdown-critical">${h}:${m}:${s}</span>`;
      }

    }, 1000, nb);
  }

  /** refresh the current login session */
  onRefresh() {

    let nb: NavigationBarComponent = this;

    nb.auth.refresh()
      .pipe(first())
      .subscribe(
        data => {

          Logger.debug(`[NavigationBar] user's session refreshed!`);

        },
        (error) => {

          nb.alerts.error(`Can not refresh session: ` + errorAsText(error), {autoClose: true});

        });
  }

}
